import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ThunkGetAppLogo } from 'store/common/thunks';
import { RootState } from 'store/index';
import { ActionsPages, Pages, VerificationPages } from 'constants/pages';
import { PaletteMode } from '@mui/material';
import { isAlphaPopupWindow } from 'components/Alpha/alphaVoicePopup';

interface InitialState {
  appLogo: string;
  page: Pages;
  verificationPage: VerificationPages;
  themeMode: PaletteMode;
  actionsPage: ActionsPages;
  showAutotaskCompanyPoliciesModal: boolean;
  showFreshServiceCompanyPoliciesModal: boolean;
  showAutotaskCompanyPolicies: boolean;
  showFreshServiceCompanyPolicies: boolean;
  platformIframeViewEnabled: boolean;
}

const initialState: InitialState = {
  appLogo: '',
  page: isAlphaPopupWindow() ? Pages.Alpha : Pages.EndUserVerification,
  verificationPage: VerificationPages.VerificationData,
  themeMode: 'light',
  actionsPage: ActionsPages.SelectActions,
  showAutotaskCompanyPoliciesModal: false,
  showFreshServiceCompanyPoliciesModal: false,
  showAutotaskCompanyPolicies: true,
  showFreshServiceCompanyPolicies: true,
  // This is a flag to enable the own platform iframe view. it`s simplified version of the pod view. ex. remove logout button
  platformIframeViewEnabled: false,
};

const commonSlice = createSlice({
  name: 'common',
  initialState,
  reducers: {
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setVerificationPage: (state, action) => {
      state.verificationPage = action.payload;
    },
    changeThemeMode: (state, action: PayloadAction<PaletteMode>) => {
      state.themeMode = action.payload;
      localStorage.setItem('theme', action.payload);
    },
    setActionsPage: (state, action: PayloadAction<ActionsPages>) => {
      state.actionsPage = action.payload;
    },
    setAutotaskPoliciesModalState: (state, action: PayloadAction<boolean>) => {
      state.showAutotaskCompanyPoliciesModal = action.payload;
    },
    setFreshServicePoliciesModalState: (state, action: PayloadAction<boolean>) => {
      state.showFreshServiceCompanyPoliciesModal = action.payload;
    },
    setShowAutotaskPolicies: (state, action: PayloadAction<boolean>) => {
      state.showAutotaskCompanyPolicies = action.payload;
    },
    setShowFreshServicePolicies: (state, action: PayloadAction<boolean>) => {
      state.showFreshServiceCompanyPolicies = action.payload;
    },
    setPlatformIframeViewEnabled: (state, action: PayloadAction<boolean>) => {
      state.platformIframeViewEnabled = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addCase(ThunkGetAppLogo.fulfilled, (state, action) => {
      state.appLogo = action.payload.data;
    });
  },
});

export default commonSlice.reducer;

export const AppLogoSelector = (state: RootState): string => state.common.appLogo;
export const AppPageSelector = (state: RootState): string => state.common.page;
export const VerificationPageSelector = (state: RootState): string => state.common.verificationPage;
export const ThemeModeSelector = (state: RootState): PaletteMode => state.common.themeMode;
export const ActionPageSelector = (state: RootState): ActionsPages => state.common.actionsPage;

export const AutotaskCompanyPoliciesSelector = (state: RootState): boolean =>
  state.common.showAutotaskCompanyPoliciesModal;

export const FreshServiceCompanyPoliciesSelector = (state: RootState): boolean =>
  state.common.showFreshServiceCompanyPoliciesModal;

export const ShowAutotaskCompanyPoliciesSelector = (state: RootState): boolean =>
  state.common.showAutotaskCompanyPolicies;
export const ShowFreshServiceCompanyPoliciesSelector = (state: RootState): boolean =>
  state.common.showFreshServiceCompanyPolicies;
export const PlatformIframeViewEnabledSelector = (state: RootState): boolean =>
  state.common.platformIframeViewEnabled;
export const {
  changeThemeMode,
  setPage,
  setVerificationPage,
  setActionsPage,
  setAutotaskPoliciesModalState,
  setFreshServicePoliciesModalState,
  setShowAutotaskPolicies,
  setShowFreshServicePolicies,
  setPlatformIframeViewEnabled,
} = commonSlice.actions;
